import {
  Button,
  CheckboxFormField,
  ClassName,
  Form,
  IFormRender,
  Label,
  TextFormField,
  Theme,
  Tray,
} from '@busybusy/webapp-react-ui';
import classNames from 'classnames';
import DashboardRibbon from 'components/foundation/dashboard/DashboardRibbon/DashboardRibbon';
import { t } from 'i18next';
import { v_require } from 'utils/validations';
import './CertificationForm.scss';
import { useRef } from 'react';
import { isNil } from 'lodash';
import HeaderText from 'containers/settings/components/HeaderText/HeaderText';

export interface ICertificationFormProps {
  onFormSubmit: (data: ICertificationFormData) => void;
  formData?: ICertificationFormData;
  onDelete?: () => void;
  edit?: boolean;
  className?: ClassName;
}

export interface ICertificationFormData {
  name?: string;
  description?: string;
  renewalRequired?: boolean;
  employeeCanManage?: boolean;
  documentationRequired?: boolean;
  expiredAfterYears?: string;
  expiredAfterMonths?: string;
}

export const CertificationForm = (props: ICertificationFormProps) => {
  const { onFormSubmit, formData, onDelete, edit, className } = props;

  const data = useRef<ICertificationFormData>(formData ?? {});

  const classes = classNames('certification-form px-6 pt-6 pb-2', className);

  function renderForm(form: IFormRender<ICertificationFormData>) {
    function getFormDisabled() {
      const months = form.state.data.expiredAfterMonths;
      const years = form.state.data.expiredAfterYears;

      return (
        (form.state.data.renewalRequired &&
          (isNil(months) || months === '0' || months === '') &&
          (isNil(years) || years === '0' || years === '')) ||
        Number(months) < 0 ||
        Number(years) < 0
      );
    }

    return (
      <>
        <Label required={true}>{t('Name')}</Label>
        <TextFormField
          name="name"
          form={form}
          restrictTo={{ maxLength: 200 }}
          validations={[{ validation: v_require }]}
        />
        <Label>{t('Description')}</Label>
        <TextFormField name="description" form={form} restrictTo={{ maxLength: 200 }} />
        <CheckboxFormField
          name="renewalRequired"
          form={form}
          label={
            <HeaderText title={t('Renewal Required')}>
              {t('The certification expires and must be periodically renewed.')}
            </HeaderText>
          }
          className="mt-4"
        />
        {form.state.data.renewalRequired && (
          <DashboardRibbon theme={Theme.LIGHT} className="mb-8">
            <Label>{t('Expires After:')}</Label>
            <Tray>
              <TextFormField
                name="expiredAfterYears"
                form={form}
                restrictTo={{ integer: true }}
                type="number"
                className="expires-field mt-2"
                placeholder="0"
              />
              <Label className="mr-8">{t('years')}</Label>
              <TextFormField
                name="expiredAfterMonths"
                form={form}
                restrictTo={{ integer: true }}
                type="number"
                className="expires-field mt-2"
                placeholder="0"
              />
              <Label>{t('months')}</Label>
            </Tray>
          </DashboardRibbon>
        )}
        <CheckboxFormField
          name="employeeCanManage"
          form={form}
          label={
            <HeaderText title={t('Employee Manage')}>
              {t('Employees can add/renew this certification themselves from their mobile device.')}
            </HeaderText>
          }
          className="mt-4"
        />
        <CheckboxFormField
          name="documentationRequired"
          form={form}
          label={
            <HeaderText title={t('Documentation Required')}>
              {t('Require a photo or PDF to be uploaded in order to record or renew this certification.')}
            </HeaderText>
          }
          className="mt-4"
        />
        <Button onClick={form.handleSubmit} disabled={getFormDisabled()} type="primary" className="my-8">
          {edit ? t('Submit') : t('Create')}
        </Button>
        {edit && (
          <Button onClick={onDelete} type="secondary" className="my-8 ml-4">
            {t('Delete')}
          </Button>
        )}
      </>
    );
  }

  return (
    <div className={classes}>
      <Form data={data.current} onSubmit={onFormSubmit} render={renderForm} />
    </div>
  );
};
